import { useAuth0 } from '@auth0/auth0-react'
import { Link } from 'react-router-dom'
import { useEffect, useState, useCallback } from 'react'
import { setupApiInterceptors } from '../services/api'
import { petService } from '../services/petService'
import { Pet } from '../types/Pet'
import LoadingSpinner from '../components/Common/LoadingSpinner'
import DetectionNetworkSignup from '../components/DetectionNetworkSignup'
import api from '../services/api'

export default function Dashboard() {
  const { user, getAccessTokenSilently } = useAuth0()
  const [pets, setPets] = useState<Pet[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setupApiInterceptors(getAccessTokenSilently)
    loadPets()
  }, [getAccessTokenSilently])

  const loadPets = async () => {
    try {
      const data = await petService.getPets()
      setPets(data)
    } catch (error) {
      console.error('Failed to load pets:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Welcome back, {user?.name || user?.email}!
        </h1>
        <p className="text-gray-600 mt-2">
          Manage your pets and keep them safe
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-700">Total Pets</h3>
          <p className="text-3xl font-bold text-primary-600 mt-2">{pets.length}</p>
        </div>
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-700">Tags Active</h3>
          <p className="text-3xl font-bold text-green-600 mt-2">
            {pets.filter(p => p.tagCode).length}
          </p>
        </div>
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-700">Lost Alerts</h3>
          <p className="text-3xl font-bold text-red-600 mt-2">
            {pets.filter(p => p.isLost).length}
          </p>
        </div>
      </div>

      <PetDetectionNetwork />

      <div className="card">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Your Pets</h2>
          <Link to="/pets" className="btn-primary">
            Manage Pets
          </Link>
        </div>

        {pets.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">You haven't added any pets yet</p>
            <Link to="/pets" className="btn-primary">
              Add Your First Pet
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pets.map(pet => (
              <div key={pet.id} className="border border-gray-200 rounded-lg p-4 flex items-center gap-4">
                {pet.photoUrl ? (
                  <img src={pet.photoUrl} alt={pet.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-400 text-xl">&#128062;</span>
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg truncate">{pet.name}</h3>
                    {pet.isLost && (
                      <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded flex-shrink-0">
                        LOST
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm">{pet.species}{pet.breed ? ` · ${pet.breed}` : ''}</p>
                  {pet.tagCode && (
                    <p className="text-green-600 text-sm">Tag: {pet.tagCode}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function PetDetectionNetwork() {
  const [optedIn, setOptedIn] = useState(false)
  const [nearbyPets, setNearbyPets] = useState<{
    id: string; name: string; species: string; lastKnownLocation?: string;
    lostSince?: string; breed?: string; photoUrl?: string; gender?: string;
    emergencyContact?: string; emergencyPhone?: string; scanCount: number;
  }[]>([])
  const [showSignup, setShowSignup] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [selectedPet, setSelectedPet] = useState<typeof nearbyPets[0] | null>(null)

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/detection-network/status')
      setOptedIn(res.data.isActive)
      if (res.data.isActive) {
        const petsRes = await api.get('/api/detection-network/nearby-lost')
        setNearbyPets(petsRes.data)
      }
    } catch { /* not joined yet */ }
  }, [])

  useEffect(() => { fetchStatus() }, [fetchStatus])

  const leaveNetwork = async () => {
    setLeaving(true)
    try {
      await api.post('/api/detection-network/leave')
      setOptedIn(false)
      setNearbyPets([])
    } catch { /* ignore */ }
    setLeaving(false)
  }

  return (
    <>
      <div className="card mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.348 14.652a3.75 3.75 0 010-5.304m5.304 0a3.75 3.75 0 010 5.304m-7.425 2.121a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.807-3.808-9.98 0-13.788m13.788 0c3.808 3.807 3.808 9.98 0 13.788M12 12h.008v.008H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Pet Detection Network</h3>
            <p className="text-sm text-gray-500">Get notified when a pet is lost within 10 miles of you</p>
          </div>
        </div>

        {optedIn ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">You're part of the network</span>
            </div>
            {nearbyPets.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500">No lost pets reported nearby</p>
              </div>
            ) : (
              <div className="space-y-2">
                {nearbyPets.map(pet => (
                  <button key={pet.id} onClick={() => setSelectedPet(pet)}
                    className="w-full flex items-center gap-3 bg-red-50 rounded-lg p-3 hover:bg-red-100 transition-colors text-left">
                    {pet.photoUrl ? (
                      <img src={pet.photoUrl} alt={pet.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <span className="text-red-500 text-lg flex-shrink-0">&#9888;</span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800">{pet.name}</p>
                      <p className="text-xs text-gray-500">{pet.lastKnownLocation || pet.species} · {pet.scanCount} scan{pet.scanCount !== 1 ? 's' : ''}</p>
                    </div>
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            )}
            <button onClick={leaveNetwork} disabled={leaving} className="text-sm text-red-500 hover:text-red-700">
              {leaving ? 'Leaving...' : 'Leave Network'}
            </button>
          </div>
        ) : (
          <button onClick={() => setShowSignup(true)} className="btn-primary w-full mt-2">
            Join the Network
          </button>
        )}
      </div>

      <DetectionNetworkSignup
        isOpen={showSignup}
        onClose={() => setShowSignup(false)}
        onJoined={() => fetchStatus()}
      />

      {selectedPet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Lost Pet</h2>
                <button onClick={() => setSelectedPet(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg mb-4">
                <p className="text-red-800 font-bold text-sm">LOST PET</p>
                {selectedPet.lostSince && (
                  <p className="text-red-700 text-xs">Missing since {new Date(selectedPet.lostSince).toLocaleDateString()}</p>
                )}
              </div>

              {selectedPet.photoUrl && (
                <img src={selectedPet.photoUrl} alt={selectedPet.name} className="w-full h-48 object-cover rounded-lg mb-4" />
              )}

              <h3 className="text-2xl font-bold text-center mb-1">{selectedPet.name}</h3>
              <p className="text-center text-gray-500 mb-4">
                {selectedPet.species}{selectedPet.breed ? ` · ${selectedPet.breed}` : ''}{selectedPet.gender ? ` · ${selectedPet.gender}` : ''}
              </p>

              {selectedPet.lastKnownLocation && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Last Known Location</p>
                  <p className="text-sm text-gray-700">{selectedPet.lastKnownLocation}</p>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Search Activity</p>
                <p className="text-sm text-gray-700">Tag scanned {selectedPet.scanCount} time{selectedPet.scanCount !== 1 ? 's' : ''}</p>
              </div>

              {(selectedPet.emergencyContact || selectedPet.emergencyPhone) && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Emergency Contact</p>
                  {selectedPet.emergencyContact && <p className="text-sm text-gray-700">{selectedPet.emergencyContact}</p>}
                  {selectedPet.emergencyPhone && (
                    <div className="flex gap-3 mt-2">
                      <a href={`tel:${selectedPet.emergencyPhone}`}
                        className="flex-1 flex items-center justify-center gap-2 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">
                        &#128222; Call
                      </a>
                      <a href={`sms:${selectedPet.emergencyPhone}`}
                        className="flex-1 flex items-center justify-center gap-2 py-2 bg-primary-400 text-white rounded-lg text-sm font-medium">
                        &#128172; Text
                      </a>
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-400 text-center">
                If you see this pet, please contact the owner or scan their tag.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
